// apps/web/src/components/chat/forms/TrialConfirmationForm.tsx
import {
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

interface MetricInfo {
  id: string;
  name: string;
  description: string | null;
  data_type: string;
}

interface TrialConfirmationData {
  submission_id: string;
  company_name: string;
  company_type: string;
  confidence: number;
  reasoning: string;
  period: string;
  metrics: MetricInfo[];
  file_name: string;
}

interface TrialConfirmationFormProps {
  data: TrialConfirmationData;
  tenantId: string;
  onSuccess: (result: { submissionId: string; action: string }) => void;
  onClose: () => void;
}

// Map company types to friendly display names
const COMPANY_TYPE_LABELS: Record<string, string> = {
  saas: 'SaaS',
  fintech: 'FinTech',
  ecommerce: 'E-Commerce',
  marketplace: 'Marketplace',
  healthcare: 'Healthcare',
  hardware: 'Hardware',
  media: 'Media',
  general: 'General',
};

// Map company types to colors
const COMPANY_TYPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'info' | 'warning'> = {
  saas: 'primary',
  fintech: 'info',
  ecommerce: 'success',
  marketplace: 'secondary',
  healthcare: 'warning',
  hardware: 'info',
  media: 'secondary',
  general: 'primary',
};

export function TrialConfirmationForm({ data, onSuccess, onClose }: TrialConfirmationFormProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRunExtraction = () => {
    setIsRunning(true);
    setError(null);

    // Return the submission ID and action to trigger the agent
    // The ChatClient will send a message to the agent with this info
    onSuccess({
      submissionId: data.submission_id,
      action: 'run_extraction',
    });
  };

  const confidencePercent = Math.round(data.confidence * 100);
  const companyTypeLabel = COMPANY_TYPE_LABELS[data.company_type] || data.company_type.toUpperCase();
  const chipColor = COMPANY_TYPE_COLORS[data.company_type] || 'primary';

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h6">Document Analysis Complete</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Company Type Card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Chip label={companyTypeLabel} color={chipColor} size="medium" />
          <Typography variant="body2" color="text.secondary">
            {confidencePercent}% confidence
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {data.reasoning}
        </Typography>
      </Paper>

      {/* File Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InsertDriveFileIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {data.file_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          &bull; {data.period}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Metrics List */}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Suggested Metrics ({data.metrics.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        These metrics will be extracted from your document:
      </Typography>

      <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1, mb: 3 }}>
        {data.metrics.map((metric) => (
          <ListItem key={metric.id} sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircleOutlineIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText
              primary={metric.name}
              secondary={metric.description}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
            <Chip label={metric.data_type} size="small" variant="outlined" sx={{ ml: 1 }} />
          </ListItem>
        ))}
      </List>

      {/* Progress */}
      {isRunning && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Running extraction...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={isRunning}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleRunExtraction} disabled={isRunning} startIcon={<AutoAwesomeIcon />}>
          {isRunning ? 'Extracting...' : 'Run Extraction'}
        </Button>
      </Box>
    </Box>
  );
}
